import React from 'react';
import { useAccountStore } from '@components/Stores/accountStore';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { useCurrencyRates } from '@hooks/useCurrencyRates';
import { useExposure } from '@hooks/useExposure';
import { MaterialTrendButton } from '../Dashboard/MaterialTrendButton';
import { getMaterialsCardData } from '../Dashboard/dashboardUtils';
import { GENERAL_ID, useExposureStore } from '@components/Stores/exposureDataStore';
import SubMenu from './components/SubMenu';
import { ExposureChart } from './components/ExposureChart';
import { StrategyDetails } from './components/StrategyDetails';
import { ExposureSummary } from './components/ExposureSummary';
import { NotificationDashboardCard } from '../Dashboard/dashboard-components/DashboardNotifications/NotificationDashboardCard';
import { useUser } from '../../hooks/useUser';
import { getNotifications } from '../../sp-firebase/notificationsFB';
import { DashboardNotification } from '@sp-types/notificationType';
import { Account, IMaterialToAccount } from '@sp-types/accountTypes';
import { BufferManagement } from './components/BufferManagement';
import { BufferStock } from './components/BufferStock';
import { useExposureHistoryByMaterial } from '../../hooks/useExposureHistory';
import { LoadingSpinnerOverlay } from '../../components/UI-Elements/LoadingSpinnerOverlay';
import { getAllInventoryReportOfAccount } from '../../sp-firebase/inventoryReportFB';
import { IInventoryReportType } from '../../sp-types/InventoryReportType';
import {
  BufferMonthData,
  calculateBufferData,
  calculateStockDeltas,
  IOrdersInventoryBal,
  TimeGranularity,
} from './components/StrategiesUtils';
import { getUnitObj } from '../../utils/MassUnits/massUnitsUtils';
import { getFutureExposure } from '../../sp-firebase/futureOrderExposureFB';
import { FutureOrderExposure } from '../../sp-types/FutureOrderType';
import { LogisticsInvManagement } from './components/LogisticsInvManagement';
import { LogisticsStock } from './components/LogisticsStock';
import { EditStrategyDialog } from './components/EditStrategyDialog';
import { updateAccount } from '../../sp-firebase/accountFB';
import { useUserAccount } from '../../hooks/useAccount';
import { useFinancialPositions } from '../../hooks/useFinancialPositions';
import { AdminBufferTable } from './components/AdminBufferTable';

const subMenuItems = [
  { key: 'buffer', label: 'Buffer stock management ' },
  { key: 'logistics', label: 'Logistic inventory management' },
  { key: 'coverage', label: 'Coverage ratio' },
  { key: 'exposure', label: 'Exposure' },
];

export const Strategies: React.FC = () => {
  const { account, materialColorDictionary: colorsMap } = useAccountStore();
  const { data: user } = useUser();
  const { data: accountData, refetch: refetchAccount } = useUserAccount(true);
  const getAccountMaterial = ({ materialsToAccount }: Account, materialId: string): IMaterialToAccount | undefined =>
    materialsToAccount.find((a) => a.materialId === materialId);

  React.useEffect(() => {
    if (user && user.isDebug && !subMenuItems.find((item) => item.key.startsWith('admin-'))) {
      // subMenuItems.push({ key: 'admin-buffer', label: 'Table buffer' });
      // subMenuItems.push({ key: 'admin-logistic', label: 'Table logistic' });
      subMenuItems.push({ key: 'admin-inv', label: 'Deltas tab' });
      subMenuItems.push({ key: 'admin-pivot', label: 'Pivot tab' });
    }
  }, [user]);

  const { materialsMap } = useMaterialsStore();
  const { convertionMap, pricesMap } = useCurrencyRates();
  const [accInventory, setAccInventory] = React.useState<IInventoryReportType[]>([]);
  const [futureOrders, setFutureOrders] = React.useState<FutureOrderExposure[]>([]);
  const [bufferData, setBufferData] = React.useState<BufferMonthData[]>([]);
  const [logisticsDeltas, setLogisticsDeltas] = React.useState<IOrdersInventoryBal[]>([]);
  const [monthDeltas, setMonthDeltas] = React.useState<IOrdersInventoryBal | undefined>();
  const [logisticsPivot, setLogisticsPivot] = React.useState<any[]>([]);
  const [logisticsTimeGranularity, setLogisticsTimeGranularity] = React.useState<TimeGranularity>('Month');
  const [showEditStrategyDialog, setShowEditStrategyDialog] = React.useState(false);

  const {
    netExposure,
    currentSelectedMaterialId: materialId,
    setCurrentSelectedMaterialId: setMaterialId,
  } = useExposureStore();
  const { refetch: fetchExposure } = useExposure(account.id);
  const [startDate] = React.useState<Date>(new Date(new Date().getFullYear(), 0, 1));

  // Fetch exposure history for the selected material
  const {
    data: exposureHistory,
    isLoading: isLoadingHistory,
    error: historyError,
    refetch: refetchHistory,
  } = useExposureHistoryByMaterial(account.id || '', materialId || '', startDate, !!account.id);
  const { data: financialPositions, refetch: refetchFinancialPositions } = useFinancialPositions();

  const [loading, setLoading] = React.useState(false);
  const [notifications, setNotifications] = React.useState<DashboardNotification[]>([]);

  const fetchInventory = async (accountId: string) => {
    const inventory = await getAllInventoryReportOfAccount(accountId, ['completed']);

    setAccInventory(inventory || []);
  };
  const fetchFutureOrders = async (accountId: string) => {
    const now = new Date();
    const orders = await getFutureExposure(accountId, new Date(now.getFullYear(), now.getMonth(), 1));

    setFutureOrders(orders || []);
  };

  const fetchNotifications = async () => {
    const notes = await getNotifications(account.id || '');

    setNotifications(notes || []);
  };

  React.useEffect(() => {
    const fetchAll = async (accountId: string) => {
      setLoading(true);
      fetchExposure();
      refetchFinancialPositions();
      await Promise.all([fetchNotifications(), fetchInventory(accountId), fetchFutureOrders(accountId)]);

      setLoading(false);
    };
    if (account.id) {
      fetchAll(account.id);
    }
  }, [account.id]);

  React.useEffect(() => {
    if (materialId && account.id) {
      refetchHistory();
    }
  }, [materialId, account.id, refetchHistory]);

  React.useEffect(() => {
    if (historyError) {
      console.error('Error loading exposure history:', historyError);
    }
  }, [exposureHistory, materialId, historyError]);

  React.useEffect(() => {
    if (!materialId) {
      const firstMaterial = account.materialsToAccount?.[1]?.materialId;
      if (firstMaterial) {
        setMaterialId(firstMaterial);
      }
    }
  }, [account, materialId]);

  const buttons = React.useMemo(
    () => getMaterialsCardData(materialsMap, colorsMap, convertionMap, account.materialsToAccount, netExposure),
    [materialsMap, colorsMap, convertionMap, account.materialsToAccount, netExposure],
  );

  const [activeSub, setActiveSub] = React.useState(subMenuItems[0].key);
  const [timeGranularity, setTimeGranularity] = React.useState<TimeGranularity>('Month');

  const handleEditStrategy = () => {
    if (['logistics', 'coverage', 'buffer'].includes(activeSub)) {
      setShowEditStrategyDialog(true);
    }
  };

  const updateMatStrategy = (
    materialToAccount: IMaterialToAccount,
    strategy: number[],
    isPercent: boolean,
    activeStrategy: string,
  ): IMaterialToAccount =>
    activeStrategy === 'logistics'
      ? { ...materialToAccount, logisticsStrategy: strategy, isPercentStrategy: isPercent }
      : activeStrategy === 'coverage'
      ? { ...materialToAccount, covRatioStrategy: strategy, covRatioIsPercent: isPercent }
      : activeStrategy === 'buffer'
      ? { ...materialToAccount, bufferStrategy: strategy[0] }
      : materialToAccount;

  const handleApplyStrategy = async (strategy: number[], isPercent: boolean) => {
    setLoading(true);

    await updateAccount(account?.id || '', {
      ...account,
      materialsToAccount: account.materialsToAccount.map((matToAcc) =>
        matToAcc.materialId === materialId ? updateMatStrategy(matToAcc, strategy, isPercent, activeSub) : matToAcc,
      ),
    });

    refetchAccount();
    // TODO: Recalculate logistics data with new percentages
    setLoading(false);
    console.log('Applied new percentages:', strategy);
  };

  const bufferDefault = 60;

  React.useEffect(() => {
    if (materialId && accInventory.length > 0) {
      if (materialId === GENERAL_ID) {
        setBufferData([]);
        return;
      }

      const accountMaterial = getAccountMaterial(account, materialId);
      const buffer = accountMaterial?.bufferStrategy || bufferDefault;
      const calculatedData = calculateBufferData(materialId, accInventory, futureOrders, buffer, timeGranularity);
      setBufferData(calculatedData);
    }
  }, [materialId, accInventory, futureOrders, account, timeGranularity]);

  React.useEffect(() => {
    if (materialId && accInventory.length > 0) {
      if (materialId === GENERAL_ID) {
        setLogisticsDeltas([]);
        setMonthDeltas(undefined);
        return;
      }
      const accountMaterial = getAccountMaterial(account, materialId);
      const isCoverage = activeSub === 'coverage';
      const buffer = accountMaterial?.bufferStrategy || bufferDefault;
      const deltasData = calculateStockDeltas(
        materialId,
        accInventory,
        futureOrders,
        financialPositions || [],
        isCoverage,
        accountMaterial?.covRatioStrategy || [],
        accountMaterial?.covRatioIsPercent || false,
        accountMaterial?.logisticsStrategy || [],
        accountMaterial?.isPercentStrategy || false,
        buffer,
        logisticsTimeGranularity,
      );
      setLogisticsDeltas(deltasData);
      // console.log('calc deltas', deltasData[0].futureOrders);
      setMonthDeltas(deltasData[0]);
      const cells = Object.keys(deltasData[0] || {});
      const pivoted: any[] = cells.map(() => ({}));
      const cellStr = 'abcdefghijklmnopqrstuvwxyz';

      logisticsDeltas.forEach((item, i) => {
        cells.forEach((cell, ci) => {
          pivoted[ci]['a'] = cell;
          pivoted[ci][cellStr[i + 1]] = item[cell as keyof IOrdersInventoryBal];
        });
      });
      setLogisticsPivot(pivoted);
    }
  }, [
    materialId,
    accInventory,
    futureOrders,
    financialPositions,
    activeSub,
    logisticsTimeGranularity,
    account,
    accountData,
  ]);

  return materialId ? (
    <div className="flex gap-3 h-full w-full">
      <div className="flex flex-col gap-3 h-full overflow-hidden">
        <MaterialTrendButton buttons={buttons} currentMaterial={materialId} setCurrentMaterial={setMaterialId} />
        <div className="flex flex-1 gap-3 overflow-hidden">
          <SubMenu items={subMenuItems} active={activeSub} onChange={setActiveSub} />
          <div className="flex-1 flex flex-col gap-3 overflow-auto bg-white">
            {activeSub === 'buffer' && (
              <BufferManagement
                materialName={materialsMap[materialId]?.name || ''}
                materialId={materialId}
                buffer={getAccountMaterial(account, materialId)?.bufferStrategy || bufferDefault}
                bufferData={bufferData}
                matPrice={pricesMap[materialId] || 1}
                unit={getUnitObj(materialsMap[materialId || '']?.unit)}
                onTimeChange={(time) => setTimeGranularity(time)}
              />
            )}
            {['logistics', 'coverage'].includes(activeSub) && (
              <LogisticsInvManagement
                key={`logistics-${materialId}-${activeSub}`}
                materialName={materialsMap[materialId]?.name || ''}
                materialId={materialId}
                logisticsData={logisticsDeltas}
                matPrice={pricesMap[materialId] || 1}
                unit={getUnitObj(materialsMap[materialId || '']?.unit)}
                onTimeChange={(time) => setLogisticsTimeGranularity(time)}
                isCoverage={activeSub === 'coverage'}
              />
            )}
            {activeSub === 'exposure' && (
              <ExposureChart
                {...{ exposureData: exposureHistory, materialId }}
                accountMaterial={getAccountMaterial(account, materialId)}
              />
            )}
            {activeSub === 'admin-inv' && (
              <div className="text-xs overflow-auto p-4">
                <AdminBufferTable rows={logisticsDeltas} />
              </div>
            )}
            {activeSub === 'admin-pivot' && (
              <div className="text-xs overflow-auto p-4">
                <AdminBufferTable rows={logisticsPivot} />
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-3 h-1/2">
          <div className="flex w-[50rem]">
            <StrategyDetails
              {...{ materialId, startDate, colorsMap, account, exposureHistory }}
              accountMaterial={getAccountMaterial(account, materialId)}
              onEditStrategy={handleEditStrategy}
            />
          </div>
          <div className="flex flex-1">
            {account && activeSub === 'exposure' && (
              <ExposureSummary
                {...{ materialId, account, netExposure, materialsMap }}
                accountMaterial={getAccountMaterial(account, materialId)}
              />
            )}
            {account && activeSub === 'buffer' && (
              <BufferStock
                bufferData={bufferData}
                matPrice={pricesMap[materialId] || 1}
                unit={getUnitObj(materialsMap[materialId || '']?.unit)}
              />
            )}
            {account && ['logistics', 'coverage'].includes(activeSub) && (
              <LogisticsStock
                currData={monthDeltas}
                matPrice={pricesMap[materialId] || 1}
                unit={getUnitObj(materialsMap[materialId || '']?.unit)}
                isCoverage={activeSub === 'coverage'}
              />
            )}
          </div>
        </div>
      </div>
      <div className="h-full w-72">
        <NotificationDashboardCard
          currentMaterialId={materialId}
          accountId={account.id}
          colorsToMaterial={colorsMap}
          notifications={notifications}
          colorsMap={colorsMap}
          isLoading={false}
          refetchNotifications={fetchNotifications}
          editEnabled={!!user?.isEditEnabled}
        />
      </div>
      {(isLoadingHistory || loading) && <LoadingSpinnerOverlay />}

      {/* Edit Strategy Dialog */}
      <EditStrategyDialog
        open={showEditStrategyDialog}
        onClose={() => setShowEditStrategyDialog(false)}
        onApply={handleApplyStrategy}
        accountMaterial={getAccountMaterial(account, materialId)}
        strategy={activeSub}
      />
    </div>
  ) : null;
};

export default Strategies;
